import { useRouter } from "next/router";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { tracking } from "shared/data";
import { PageType } from "shared/lib/page-type";

import { hasPostiesQuiz } from "scmp-app/components/article/article-render/posties/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { StyledQuizFlag } from "scmp-app/components/content/content-item-render/variants/posties-secondary/styles";
import type { postiesSectionSecondaryContentItemContent$key } from "scmp-app/queries/__generated__/postiesSectionSecondaryContentItemContent.graphql";

import {
  Container,
  Content,
  DateContainer,
  Headline,
  StyledArrowButton,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledOffsetContainer,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: postiesSectionSecondaryContentItemContent$key;
  showQuizFlag?: boolean;
};

export const ContentItemPostiesSectionSecondary = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, showQuizFlag }, reference) => {
    const router = useRouter();

    const postiesSectionQueryParameters = {
      module: (router.query.name as string).toLowerCase().replaceAll(/[\s-]+/gi, "_"),
      pgtype: tracking.pageType.SectionPosties,
    };

    const content = useFragment(
      graphql`
        fragment postiesSectionSecondaryContentItemContent on Content {
          ...entityLink
          ...contentCoverImageContent @arguments(withSize320x240: true, withSize540x360: true)
          ...hooksContentItemProvidersContent
            @arguments(withHeadline: true, withPublishedDate: true)
          ... on Article {
            ...helpersHasPostiesQuiz
          }
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);
    const hasQuiz = showQuizFlag ? hasPostiesQuiz(content) : false;

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink
          query={{ pageType: PageType.PostiesArticle, ...postiesSectionQueryParameters }}
          reference={content}
        >
          <StyledOffsetContainer borderRadius="12px">
            <StyledContentCoverImage
              reference={content}
              responsiveVariants={{
                desktopUp: "size320x240",
                mobileUp: "size540x360",
                tabletUp: "size320x240",
              }}
            />
            {hasQuiz && <StyledQuizFlag />}
          </StyledOffsetContainer>
          <Content>
            <Headline>
              {providers.headline({
                hideTag: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <DateContainer>{providers.publishedDate({})}</DateContainer>
            <StyledArrowButton />
          </Content>
        </StyledEntityLink>
      </Container>
    );
  },
);

ContentItemPostiesSectionSecondary.displayName = "ContentItemPostiesSectionSecondary";
